import { useMemo } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery, type CachedQueryStatus } from "./useCachedQuery";
import type { SubscriptionSummary } from "../types/billing";

// Fleet billing list for the admin Billing tab: all subscriptions newest-first,
// with optional email / tenant search. The unfiltered view seeds from the
// shared admin:billing cache; any active filter is network-only (keeps the
// previous rows on screen while the search resolves), matching the SWR pattern.

export type BillingListQuery = {
  readonly email?: string;
  readonly tenantId?: string;
};

const buildUrl = (query: BillingListQuery): string => {
  const params = new URLSearchParams({ limit: "100" });
  if (query.email && query.email.trim().length > 0) params.set("email", query.email.trim());
  if (query.tenantId && query.tenantId.trim().length > 0) params.set("tenantId", query.tenantId.trim());
  return `/api/admin/billing/subscriptions?${params.toString()}`;
};

export type UseAdminBillingListResult = {
  readonly status: CachedQueryStatus;
  readonly subscriptions: SubscriptionSummary[];
  readonly error: string | null;
  readonly isFetching: boolean;
  readonly refresh: () => void;
};

export const useAdminBillingList = (query: BillingListQuery = {}): UseAdminBillingListResult => {
  const url = buildUrl(query);
  const isDefault = !query.email?.trim() && !query.tenantId?.trim();

  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<SubscriptionSummary[]> => {
      const res = await fetch(url, { credentials: "include", signal });
      if (!res.ok) throw new Error(`fetch failed (${res.status})`);
      return (await res.json()) as SubscriptionSummary[];
    },
    [url],
  );

  const { status, data, error, isFetching, refetch } = useCachedQuery<SubscriptionSummary[]>({
    cacheResource: isDefault ? ADMIN_QUERIES.billing.resource : null,
    queryKey: url,
    fetcher,
  });

  return { status, subscriptions: data ?? [], error, isFetching, refresh: () => void refetch() };
};
